import { parseLocalDate } from '@frontend/helpers/dateHelpers';
import type { NewDateLock } from './TimesheetDayLockService';

/** Sunday = 0 … Saturday = 6 — `tenants.workweek_start_day` and `Date.getDay()`. */
export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * The weekday of a `yyyy-MM-dd` key, read as a LOCAL calendar date (never a UTC parse, which would
 * shift a Calgary date — plan hazard H3). NaN-safe: an unparseable key returns null.
 */
export function weekdayOfDateKey(dateKey: string): number | null {
  const d = parseLocalDate(dateKey);
  return Number.isNaN(d.getTime()) ? null : d.getDay();
}

export const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-11-05` → `Nov 5`, read from the key itself (never a UTC parse — plan hazard HD3). */
export function formatMonthDay(dateKey: string): string {
  const month = Number(dateKey.slice(5, 7));
  const day = Number(dateKey.slice(8, 10));
  return month >= 1 && month <= 12 && day >= 1 ? `${MONTH_ABBR[month - 1]} ${day}` : dateKey;
}

/** `Nov 5 every year` for a recurrence, `Thu, Nov 5, 2026` for a one-off date lock. */
export function describeDateLock(lock: { date: string | null; month: number | null; day: number | null }): string {
  if (lock.date) {
    const weekday = weekdayOfDateKey(lock.date);
    const prefix = weekday === null ? '' : `${WEEKDAY_NAMES[weekday].slice(0, 3)}, `;
    return `${prefix}${formatMonthDay(lock.date)}, ${lock.date.slice(0, 4)}`;
  }
  return `${MONTH_ABBR[(lock.month ?? 1) - 1]} ${lock.day} every year`;
}

/**
 * The lock the Locked days drawer creates for `dateKey`
 * (plans/todo/timesheet-lock-days-from-drawer.md, F1): `{ date, label }` for this date only, or
 * `{ month, day, label }` every year. `what` is how the toasts name it — `Thu, Nov 5, 2026` or
 * `Nov 5 every year` — and is the label when none is typed. Month and day come from the key itself
 * (never a UTC parse — plan hazard HD3).
 */
export function newDateLockFor(dateKey: string, shape: 'date' | 'annual', label: string): { lock: NewDateLock; what: string } {
  const what = shape === 'date' ? describeDateLock({ date: dateKey, month: null, day: null }) : `${formatMonthDay(dateKey)} every year`;
  const text = label.trim() || what;
  const [, month, day] = dateKey.split('-').map(Number);
  return { lock: shape === 'date' ? { date: dateKey, label: text } : { month, day, label: text }, what };
}

/** The server's own sentence from an HttpError (`data.error`), or null. */
export function serverErrorMessage(err: unknown): string | null {
  const data = (err as { data?: { error?: unknown; message?: unknown } } | null)?.data;
  const message = data?.error ?? data?.message;
  return typeof message === 'string' && message.trim() ? message : null;
}

/**
 * True when the server refused because a day IS locked, or because it is NOT locked any more. Either
 * way this tab's lock state is stale, so the caller refetches it (plan hazard H11).
 */
export function isDayLockMessage(message: string | null | undefined): boolean {
  if (!message) return false;
  return /This day is locked|day is not locked|no longer locked/i.test(message);
}

/** `2.5` → `2:30`, the grids' total format. */
export function formatRequestHours(hours: number | null | undefined): string {
  const value = Math.max(0, Number(hours) || 0);
  let h = Math.floor(value);
  let m = Math.round((value - h) * 60);
  if (m === 60) {
    h += 1;
    m = 0;
  }
  return `${h}:${String(m).padStart(2, '0')}`;
}

/** The grid-cell key a request belongs to (employee, project, code, day). */
export function requestCellKey(employeeId: string, matterId: string, costCode: string, dateKey: string): string {
  return `${employeeId}::${matterId}::${costCode}::${dateKey}`;
}
